/**
 * PDF OCR extraction against the NestJS backend's case-file pipeline.
 *
 * Replaces the browser-side Gemini call that used to live in PdfExtractor.
 * The pipeline is asynchronous and produces Markdown, so this module owns the
 * whole four-leg dance and reports progress as it goes:
 *
 *   create-case → prepare-document-upload → PUT to S3 → complete-document-upload
 *   → poll /files until terminal → GET the presigned `.extracted.md`
 *
 * Authenticated calls go through `backendApi`'s baseQuery (Firebase ID token +
 * x-office-id). The S3 legs use a bare `fetch`: a presigned URL carries its own
 * signature and any extra header — Authorization included — invalidates it.
 */
import type {
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import type { QueryReturnValue } from "@reduxjs/toolkit/query";
import { backendApi } from "./backendApi.rtkq";
import { trackOcrReextractionAttempted } from "@/lib/mixpanel";

/** Mirrors TERMINAL_PROCESSING_STAGES in the backend's ocr-processing-stage.util.ts. */
const TERMINAL_STAGES = new Set(["ocr_worker_done", "ocr_worker_failed", "ocr_worker_partial_failed"]);

const POLL_INTERVAL_MS = 3000;
/**
 * A large scanned PDF is OCR'd page by page and legitimately runs for minutes.
 * The MCP client's 120s default is far too short for a user-facing sidebar.
 */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;
const PRESIGNED_FETCH_TIMEOUT_MS = 60 * 1000;

const APP_ID = "scan-documents-extraction";

export type OcrStep = "preparing" | "uploading" | "queued" | "extracting" | "fetching";

export interface OcrProgress {
  step: OcrStep;
  /** Hebrew, ready to render. */
  activity: string;
  percent: number;
  pagesProcessed?: number;
  pagesTotal?: number;
}

export interface ExtractPdfArgs {
  file: File;
  /** The Postgres office id — same value backendApi sends as x-office-id. */
  officeId: string;
  signal?: AbortSignal;
  onProgress?: (progress: OcrProgress) => void;
}

export interface ExtractPdfResult {
  markdown: string;
  caseId: string;
  fileId: string;
  pagesTotal?: number;
  pagesFailed?: number;
  /** True when served from a previous extraction of the same file, with no new OCR. */
  reused?: boolean;
  /** Nest checksum reuse: pages copied from an identical file already OCR'd in the office. */
  reusedFrom?: { fileId: string; fileName: string; caseId: string };
  /** Set when a file of the same name already existed and the upload was stored under another name. */
  renamedTo?: string;
}

/** Same cap as backend DEFAULT_DOCUMENT_MAX_UPLOAD_BYTES. A file of this exact size is accepted. */
export const DOCUMENT_MAX_UPLOAD_BYTES = 300 * 1024 * 1024;

/** Thrown for conditions PdfExtractor renders as a specific Hebrew message. */
export class OcrError extends Error {
  constructor(
    message: string,
    readonly code:
      | "cancelled"
      | "no_office"
      | "file_too_large"
      | "upload_blocked"
      | "upload_failed"
      | "ocr_failed"
      | "timeout"
      | "empty"
      | "backend"
      | "insufficient_credits",
  ) {
    super(message);
    this.name = "OcrError";
  }
}

interface FileDetail {
  fileName: string;
  s3Key: string;
  fileId?: string;
  status: string;
  errorMessage?: string;
  processingStage?: string;
  extractedTextUrl?: string;
  pagesProcessed?: number;
  pagesTotal?: number;
  pagesFailed?: number;
}

interface FilesListResponse {
  caseId: string;
  files: FileDetail[];
}

interface PrepareUploadResponse {
  fileId: string;
  presignedUploadUrl: string;
  uploadContentType: string;
  uploadHeaders: Record<string, string>;
}

/**
 * The baseQuery handed to a queryFn: issues authenticated calls via backendApi.
 * Meta is `{}` to match backendApi's BaseQueryFn (5th generic defaults to {}).
 */
type BaseQuery = (
  args: string | FetchArgs,
) =>
  | QueryReturnValue<unknown, FetchBaseQueryError, {}>
  | PromiseLike<QueryReturnValue<unknown, FetchBaseQueryError, {}>>;

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new OcrError("Extraction cancelled", "cancelled");
  }
};

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new OcrError("Extraction cancelled", "cancelled"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * `user_uploads.caseId` is a GLOBAL varchar(36) primary key — it is not scoped
 * by office — so a fixed literal id would collide between offices. Deriving it
 * from the office keeps the scratch case both stable and unique, with no extra
 * state to store anywhere.
 */
export const resolveScratchCaseId = async (officeId: string): Promise<string> => {
  const bytes = new TextEncoder().encode(`${officeId}:${APP_ID}`);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 32);
};

const isInsufficientCreditsResponse = (error: unknown): boolean => {
  const status = (error as { status?: number })?.status;
  const data = (error as { data?: { code?: string } })?.data;
  return status === 402 || data?.code === "INSUFFICIENT_CREDITS";
};

const errorMessageOf = (error: unknown): string => {
  const data = (error as { data?: unknown })?.data;
  if (typeof data === "string") return data;
  const message = (data as { message?: unknown })?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message)) return message.join(", ");
  // Nest nests its own BadRequest payloads one level deep on some routes.
  const nested = (message as { message?: unknown })?.message;
  if (typeof nested === "string") return nested;
  return `Backend error (${String((error as { status?: unknown })?.status ?? "unknown")})`;
};

/**
 * Provision the office's scratch case, once. `createCase` accepts an explicit
 * caseId and 400s when it already exists for the office — which is the steady
 * state after the first extraction, so that 400 is success, not failure.
 */
const ensureScratchCase = async (baseQuery: BaseQuery, caseId: string, officeId: string): Promise<void> => {
  const result = await baseQuery({
    url: "/api/user-upload/create-case",
    method: "POST",
    body: {
      caseId,
      clientId: officeId,
      clientName: "Scan Documents Extraction",
      caseDescription: "Standalone PDF extractions from the Legalix suite",
    },
  });

  if (!result.error) return;

  const status = (result.error as { status?: number }).status;
  if (status === 400 && /already exists/i.test(errorMessageOf(result.error))) {
    return;
  }
  if (status === 403) {
    throw new OcrError(errorMessageOf(result.error), "no_office");
  }
  throw new OcrError(errorMessageOf(result.error), "backend");
};

const buildActivity = (pagesProcessed?: number, pagesTotal?: number): string => {
  if (pagesTotal && pagesTotal > 0) {
    return `מחלץ טקסט — ${pagesProcessed ?? 0} מתוך ${pagesTotal} עמודים`;
  }
  return "מחלץ טקסט מהמסמך…";
};

const percentOf = (pagesProcessed?: number, pagesTotal?: number): number =>
  Math.min(99, Math.round(((pagesProcessed ?? 0) / Math.max(pagesTotal ?? 0, 1)) * 100));

/** Look up an already-OCR'd file in the case by name. Returns undefined if absent. */
const findExtractedFile = async (
  baseQuery: BaseQuery,
  caseId: string,
  fileName: string,
): Promise<FileDetail | undefined> => {
  const listed = await baseQuery({
    url: `/api/user-upload/${encodeURIComponent(caseId)}/files`,
    method: "GET",
  });
  if (listed.error) return undefined; // Not fatal — fall through to a normal upload.
  const files = (listed.data as FilesListResponse)?.files ?? [];
  return files.find((f) => f.fileName === fileName && f.extractedTextUrl);
};

const fetchMarkdown = async (url: string, signal?: AbortSignal): Promise<string> => {
  const response = await fetch(url, {
    signal: signal ?? AbortSignal.timeout(PRESIGNED_FETCH_TIMEOUT_MS),
  });
  if (!response.ok) {
    throw new OcrError(`Extracted text fetch failed (${response.status})`, "ocr_failed");
  }
  const markdown = (await response.text()).trim();
  if (!markdown) {
    throw new OcrError("Extraction produced no text", "empty");
  }
  return markdown;
};

export const ocrApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    assertCanCharge: builder.mutation<
      { allowed: true; appId: string; units: number },
      { appId: string; units: number }
    >({
      query: (body) => ({
        url: "/usage/assert-can-charge",
        method: "POST",
        body,
      }),
    }),
    extractPdf: builder.mutation<ExtractPdfResult, ExtractPdfArgs>({
      queryFn: async ({ file, officeId, signal, onProgress }, _queryApi, _extraOptions, baseQuery) => {
        // Tracked out here so the outer catch can release a reserved-but-never-
        // completed file: a cancel between `prepare` and `complete` would
        // otherwise strand an upload_pending row and an orphaned S3 object.
        let pendingCaseId: string | undefined;
        let pendingFileId: string | undefined;

        try {
          if (!officeId) {
            throw new OcrError("No office context", "no_office");
          }
          if (file.size > DOCUMENT_MAX_UPLOAD_BYTES) {
            throw new OcrError("File exceeds the 300 MB upload limit", "file_too_large");
          }

          throwIfAborted(signal);
          onProgress?.({ step: "preparing", activity: "מכין העלאה…", percent: 2 });

          const caseId = await resolveScratchCaseId(officeId);
          await ensureScratchCase(baseQuery, caseId, officeId);
          throwIfAborted(signal);

          // 0. Reuse a previous extraction of the same file.
          //
          // The backend dedupes by (caseId, fileName) on purpose: re-uploading
          // a file resolves to the same fileId, and its OCR task name is
          // derived from that, so a second enqueue returns ALREADY_EXISTS and
          // is correctly treated as success — the pipeline will not OCR the
          // same document twice. That means re-uploading must serve the text
          // that already exists rather than wait for work that will never run.
          //
          // This has to happen BEFORE prepare-document-upload: that call resets
          // the row's metadata to `upload_pending`, dropping the link to the
          // extracted context.
          const existing = await findExtractedFile(baseQuery, caseId, file.name);
          if (existing?.extractedTextUrl) {
            trackOcrReextractionAttempted({
              officeId,
              fileSizeBytes: file.size,
              existingPagesTotal: existing.pagesTotal,
            });
            onProgress?.({ step: "fetching", activity: "טוען חילוץ קיים…", percent: 99 });
            const cached = await fetchMarkdown(existing.extractedTextUrl, signal);
            return {
              data: {
                markdown: cached,
                caseId,
                fileId: existing.fileId as string,
                pagesTotal: existing.pagesTotal,
                pagesFailed: existing.pagesFailed,
                reused: true,
              },
            };
          }

          // 1. Reserve a file slot and get the presigned PUT.
          const prepared = await baseQuery({
            url: "/api/user-upload/prepare-document-upload",
            method: "POST",
            body: {
              caseId,
              fileName: file.name,
              mimeType: file.type || "application/pdf",
              // Load-bearing: complete-upload rejects the file outright when the
              // object's actual length doesn't match what was declared here.
              sizeBytes: file.size,
            },
          });
          if (prepared.error) {
            throw new OcrError(errorMessageOf(prepared.error), "backend");
          }
          const { fileId, presignedUploadUrl, uploadContentType, uploadHeaders } =
            prepared.data as PrepareUploadResponse;
          pendingCaseId = caseId;
          pendingFileId = fileId;
          throwIfAborted(signal);

          // 2. PUT the raw bytes straight to S3.
          onProgress?.({ step: "uploading", activity: "מעלה את הקובץ…", percent: 8 });
          const putHeaders: Record<string, string> = { ...(uploadHeaders || {}) };
          if (uploadContentType && !putHeaders["Content-Type"]) {
            putHeaders["Content-Type"] = uploadContentType;
          }

          try {
            const putResponse = await fetch(presignedUploadUrl, {
              method: "PUT",
              headers: putHeaders,
              body: file,
              signal,
            });
            if (!putResponse.ok) {
              throw new OcrError(`PUT ${putResponse.status}`, "upload_failed");
            }
          } catch (error) {
            if (signal?.aborted) throw new OcrError("Extraction cancelled", "cancelled");
            if (error instanceof OcrError) throw error;
            // A bucket CORS rejection arrives as a bare TypeError with no status,
            // identical in shape to a genuine network failure. Naming it here is
            // the difference between a five-minute fix and an invisible one.
            throw new OcrError(
              (error as Error)?.message || "Upload failed",
              error instanceof TypeError ? "upload_blocked" : "upload_failed",
            );
          }
          throwIfAborted(signal);

          // 3. Hand it to the pipeline.
          onProgress?.({ step: "queued", activity: "הקובץ הועלה, ממתין לעיבוד…", percent: 12 });
          const completed = await baseQuery({
            url: "/api/user-upload/complete-document-upload",
            method: "POST",
            body: { caseId, fileId },
          });
          if (completed.error) {
            if (isInsufficientCreditsResponse(completed.error)) {
              throw new OcrError("INSUFFICIENT_CREDITS", "insufficient_credits");
            }
            throw new OcrError(errorMessageOf(completed.error), "backend");
          }
          const completedData = completed.data as
            | {
                renamedTo?: string;
                reusedFrom?: { fileId: string; fileName: string; caseId: string };
              }
            | undefined;
          const renamedTo = completedData?.renamedTo;
          const reusedFrom = completedData?.reusedFrom;
          // The pipeline owns the file now; failing it here would be wrong.
          pendingCaseId = undefined;
          pendingFileId = undefined;

          // 4. Poll until the file reaches a terminal stage.
          const startedAt = Date.now();
          let detail: FileDetail | undefined;

          while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
            throwIfAborted(signal);
            await sleep(POLL_INTERVAL_MS, signal);

            const listed = await baseQuery({
              url: `/api/user-upload/${encodeURIComponent(caseId)}/files`,
              method: "GET",
            });
            if (listed.error) {
              throw new OcrError(errorMessageOf(listed.error), "backend");
            }

            const files = (listed.data as FilesListResponse)?.files ?? [];
            detail = files.find((f) => f.fileId === fileId);
            if (!detail) continue;

            onProgress?.({
              step: "extracting",
              activity: buildActivity(detail.pagesProcessed, detail.pagesTotal),
              percent: percentOf(detail.pagesProcessed, detail.pagesTotal),
              pagesProcessed: detail.pagesProcessed,
              pagesTotal: detail.pagesTotal,
            });

            if (detail.processingStage && TERMINAL_STAGES.has(detail.processingStage)) {
              break;
            }
            detail = undefined;
          }

          if (!detail) {
            throw new OcrError("OCR did not finish in time", "timeout");
          }
          if (detail.processingStage === "ocr_worker_failed") {
            throw new OcrError(detail.errorMessage || "OCR failed", "ocr_failed");
          }
          if (!detail.extractedTextUrl) {
            throw new OcrError("Backend returned no extracted text URL", "ocr_failed");
          }

          // 5. Read the Markdown the worker archived.
          onProgress?.({
            step: "fetching",
            activity: "טוען את הטקסט שחולץ…",
            percent: 99,
            pagesProcessed: detail.pagesProcessed,
            pagesTotal: detail.pagesTotal,
          });

          const markdown = await fetchMarkdown(detail.extractedTextUrl, signal);

          return {
            data: {
              markdown,
              caseId,
              fileId,
              pagesTotal: detail.pagesTotal,
              pagesFailed: detail.pagesFailed,
              renamedTo,
              ...(reusedFrom ? { reused: true, reusedFrom } : {}),
            },
          };
        } catch (error) {
          if (pendingCaseId && pendingFileId) {
            // Best effort: release the reservation so a failed or cancelled
            // upload doesn't leave a pending row and an orphaned S3 object.
            try {
              await baseQuery({
                url: "/api/user-upload/fail-document-upload",
                method: "POST",
                body: {
                  caseId: pendingCaseId,
                  fileId: pendingFileId,
                  errorMessage: (error as Error)?.message || "Browser upload did not complete",
                },
              });
            } catch {
              // Compensation is best effort — never mask the original failure.
            }
          }
          if (error instanceof OcrError) {
            return { error: { code: error.code, message: error.message } as any };
          }
          if ((error as Error)?.name === "AbortError") {
            return { error: { code: "cancelled", message: "Extraction cancelled" } as any };
          }
          console.error("extractPdf failed:", error);
          return { error: { code: "backend", message: (error as Error)?.message ?? "Unknown error" } as any };
        }
      },
    }),
  }),
});

export const { useExtractPdfMutation, useAssertCanChargeMutation } = ocrApi;
